class BundleCartValidator {
  // ─── Bundle Product IDs ────────────────────────────────────────
  // Add or remove product IDs here as needed.
  // Find the ID in the product URL on Shopify admin:
  // admin.shopify.com/store/direct-colors/products/XXXXXXXXXXXXXXX
  //
  // Static so other features can read the same list without constructing a
  // validator. The cart recovery landing uses it to refuse to rebuild these
  // products, since a cart permalink cannot carry the line item properties
  // they require and this class would then delete them.
  static PRODUCT_IDS = [
    14938383155565, //XDELETE Bundle TEST PRODUCT
    14907248804205, //AcquaTint™ Trial System
    14907247198573, //Antiquing™ 2-Step Trial System
    14907248935277, //ColorWave® 2-Step Trial System
    14907249066349, //DecoGel™ 3-Step Trial System
    14907249131885, //EasyTint™ Trial System
    14943670042989, //EverStain™ 3-Step Trial System
    14932650066285, //PaveWave™ 2-Step Trial System
    14907249295725, //Portico™ 2-Step Trial System
    14907249328493, //Vibrance™ Dye 2-Step Trial System
    14969860489581, //ColorBlast™ 5-Color Pigment Set
    14969885196653, //Concrete Pigment Sampler
  ];
  // ──────────────────────────────────────────────────────────────

  // Kept for backwards compatibility with any instance-level reads.
  BUNDLE_PRODUCT_IDS = BundleCartValidator.PRODUCT_IDS;

  constructor() {
    document.addEventListener('cart:updated', this.onCartUpdated.bind(this));
    this.injectToastStyles();
    this.validateOnLoad();
  }

  async validateOnLoad() {
    this.blockCheckout();
    await new Promise(resolve => setTimeout(resolve, 300));

    const response = await fetch(`${FoxTheme.routes.cart_url}`, {
      ...FoxTheme.utils.fetchConfig()
    });
    const cart = await response.json();
    await this.checkForInvalidBundles(cart);
    this.unblockCheckout();
  }

  onCartUpdated(event) {
    if (event.detail?.cart) {
      this.blockCheckout();
      this.checkForInvalidBundles(event.detail.cart).then(() => {
        this.unblockCheckout();
      });
    }
  }

  async checkForInvalidBundles(cart) {
    if (!cart.items || cart.items.length === 0) return;

    const invalidItems = [];

    for (const item of cart.items) {
      const isBundle = this.BUNDLE_PRODUCT_IDS.includes(item.product_id);

      if (!isBundle) continue;

      const hasValidProperties = item.properties &&
        Object.keys(item.properties).length > 0 &&
        Object.values(item.properties).some(v => v && v.toString().trim() !== '');

      if (!hasValidProperties) {
        invalidItems.push(item);
      }
    }

    if (invalidItems.length > 0) {
      await this.removeInvalidItems(invalidItems);
    }
  }

  async removeInvalidItems(invalidItems) {
    for (const item of invalidItems) {
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      const body = JSON.stringify({
        id: item.key,
        quantity: 0,
        sections: sectionsToBundle,
      });

      const response = await fetch(FoxTheme.routes.cart_change_url, {
        ...FoxTheme.utils.fetchConfig(),
        body
      });
      const parsedState = await response.json();

      FoxTheme.pubsub.publish(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        { cart: parsedState }
      );
    }

    this.showToast(invalidItems);
  }

  blockCheckout() {
    const btns = document.querySelectorAll('[name="checkout"], a[href="/checkout"]');
    btns.forEach(btn => {
      btn.setAttribute('disabled', true);
      btn.setAttribute('aria-disabled', 'true');
      btn.dataset.bundleValidating = 'true';
    });
  }

  unblockCheckout() {
    document.querySelectorAll('[data-bundle-validating]').forEach(btn => {
      btn.removeAttribute('data-bundle-validating');
      // Only re-enable if PigmentMinQtyValidator is also done
      if (!btn.hasAttribute('data-pigment-validating')) {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
      }
    });
  }

  injectToastStyles() {
    if (document.getElementById('bundle-validator-styles')) return;

    const style = document.createElement('style');
    style.id = 'bundle-validator-styles';
    style.textContent = `
      .bv-toast {
        position: fixed;
        bottom: 24px;
        right: 24px;
        z-index: 9999;
        max-width: 360px;
        background: #ffffff;
        border: 0.5px solid rgba(0,0,0,0.12);
        border-radius: 12px;
        padding: 16px 18px;
        box-shadow: 0 2px 12px rgba(0,0,0,0.08);
        display: flex;
        flex-direction: column;
        gap: 12px;
        opacity: 0;
        transform: translateY(12px);
        transition: opacity 0.25s ease, transform 0.25s ease;
        pointer-events: none;
        font-family: inherit;
      }
      .bv-toast.is-visible {
        opacity: 1;
        transform: translateY(0);
        pointer-events: auto;
      }
      .bv-toast__header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
      }
      .bv-toast__icon-body {
        display: flex;
        align-items: flex-start;
        gap: 10px;
      }
      .bv-toast__icon {
        font-size: 16px;
        line-height: 1;
        margin-top: 1px;
        flex-shrink: 0;
      }
      .bv-toast__title {
        font-size: 14px;
        font-weight: 500;
        color: #1a1a1a;
        margin: 0 0 4px;
        font-family: inherit;
      }
      .bv-toast__body {
        font-size: 13px;
        color: #6b6b6b;
        margin: 0;
        line-height: 1.5;
        font-family: inherit;
      }
      .bv-toast__body strong {
        color: #1a1a1a;
        font-weight: 500;
      }
      .bv-toast__close {
        background: none;
        border: none;
        cursor: pointer;
        color: #9b9b9b;
        font-size: 16px;
        padding: 0;
        line-height: 1;
        flex-shrink: 0;
        font-family: inherit;
        align-self: flex-start;
        margin-top: -12px;
      }
      .bv-toast__close:hover {
        color: #1a1a1a;
      }
      .bv-toast__links {
        border-top: 0.5px solid rgba(0,0,0,0.08);
        padding-top: 12px;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .bv-toast__link {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        color: #1a1a1a;
        text-decoration: none;
        border: 0.5px solid rgba(0,0,0,0.18);
        border-radius: 999px;
        padding: 6px 12px;
        width: fit-content;
        font-family: inherit;
        transition: background 0.15s ease;
      }
      .bv-toast__link:hover {
        background: rgba(0,0,0,0.04);
      }
      @media (max-width: 640px) {
        .bv-toast {
          bottom: 16px;
          right: 16px;
          left: 16px;
          max-width: 100%;
        }
      }
    `;
    document.head.appendChild(style);
  }

  showToast(invalidItems) {
    document.querySelector('.bv-toast')?.remove();

    const isSingle = invalidItems.length === 1;
    const title = isSingle
      ? 'Product removed from cart'
      : `${invalidItems.length} products removed from cart`;
    const body = isSingle
      ? `<strong>${invalidItems[0].product_title}</strong> requires color/option selection before purchasing.`
      : `These products require color/option selection before purchasing.`;

    const toast = document.createElement('div');
    toast.className = 'bv-toast';
    toast.innerHTML = `
      <div class="bv-toast__header">
        <div class="bv-toast__icon-body">
          <span class="bv-toast__icon">⚠️</span>
          <div>
            <p class="bv-toast__title">${title}</p>
            <p class="bv-toast__body">${body}</p>
          </div>
        </div>
        <button class="bv-toast__close" aria-label="Close">✕</button>
      </div>
      <div class="bv-toast__links">
        ${invalidItems.map(item => `
          <a class="bv-toast__link" href="${item.url}">
            Customize ${item.product_title}
          </a>
        `).join('')}
      </div>
    `;

    toast.querySelector('.bv-toast__close').addEventListener('click', () => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    });

    document.body.appendChild(toast);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => toast.classList.add('is-visible'));
    });

    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 6000);
  }
}

// A class declaration is a lexical global, not a property of window, so it is
// published explicitly for scripts that cannot rely on execution order.
window.BundleCartValidator = BundleCartValidator;

document.addEventListener('DOMContentLoaded', () => {
  const isCartPage = window.location.pathname.includes('/cart');
  const hasCartDrawer = document.querySelector('cart-drawer');

  if (isCartPage || hasCartDrawer) {
    new BundleCartValidator();
  }
});